<script setup lang="ts">
// ショートカット一覧 + 再割り当て。行の「変更」を押して次のキー入力を取り込む。
import { onBeforeUnmount, ref } from 'vue'
import { useModal } from '@/composables/useModalStack'
import { useI18n, type I18nKey } from '@/i18n'
import { SHORTCUTS, TOOL_KEYS, formatKeys, keysFor, setShortcutKeys, resetShortcuts, comboOf, shortcutOverrides } from '@/composables/useShortcuts'
import { IMPLEMENTED_TOOLS } from '@/stores/uiStore'

const emit = defineEmits<{ close: [] }>()
useModal('shortcuts')
const { t } = useI18n()
const toolEntries = Object.entries(TOOL_KEYS).filter(([, id]) => IMPLEMENTED_TOOLS.has(id))
const capturing = ref<string | null>(null)

function onCapture(e: KeyboardEvent) {
  if (!capturing.value) return
  e.preventDefault()
  e.stopPropagation()
  const k = e.key
  if (k === 'Escape') {
    capturing.value = null
    return
  }
  if (['Shift', 'Meta', 'Control', 'Alt'].includes(k)) return
  setShortcutKeys(capturing.value, [comboOf(e)])
  capturing.value = null
}
function startCapture(id: string) {
  capturing.value = id
}
function clearKeys(id: string) {
  setShortcutKeys(id, [])
}
window.addEventListener('keydown', onCapture, true)
onBeforeUnmount(() => window.removeEventListener('keydown', onCapture, true))
function onEsc() {
  if (capturing.value) capturing.value = null
  else emit('close')
}
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="emit('close')">
    <div class="modal shortcuts" @keydown.esc="onEsc">
      <h2>{{ t('menu.help.shortcuts') }}</h2>
      <p class="muted hint">{{ t('shortcuts.editHint') }}</p>
      <div class="scroll">
        <table>
          <tr v-for="s in SHORTCUTS" :key="s.id" :class="{ changed: shortcutOverrides[s.id] !== undefined }">
            <td>{{ t(s.label) }}</td>
            <td class="keys">
              <template v-if="capturing === s.id"><span class="press">{{ t('shortcuts.press') }}</span></template>
              <template v-else>
                <kbd v-for="k in keysFor(s)" :key="k" @click="startCapture(s.id)">{{ formatKeys(k) }}</kbd>
                <button class="ghost tiny" :title="t('shortcuts.change')" @click="startCapture(s.id)">✎</button>
                <button v-if="keysFor(s).length" class="ghost tiny" title="×" @click="clearKeys(s.id)">×</button>
              </template>
            </td>
          </tr>
          <tr><td>{{ t('shortcut.hand') }}</td><td class="keys"><kbd>Space</kbd></td></tr>
          <tr v-for="[k, id] in toolEntries" :key="id">
            <td>{{ t(`tool.${id}` as I18nKey) }}</td>
            <td class="keys"><kbd>{{ k.toUpperCase() }}</kbd></td>
          </tr>
        </table>
      </div>
      <div class="actions">
        <button @click="resetShortcuts()">{{ t('shortcuts.reset') }}</button>
        <span class="spacer" />
        <button class="primary" @click="emit('close')">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shortcuts {
  min-width: 460px;
}
.hint {
  font-size: 11px;
  margin: 0 0 6px;
}
.scroll {
  max-height: 60vh;
  overflow: auto;
}
table {
  border-collapse: collapse;
  width: 100%;
}
td {
  padding: 3px 8px;
  border-bottom: 1px solid var(--line-weak);
}
.keys {
  text-align: right;
  white-space: nowrap;
}
.keys kbd {
  margin-left: 6px;
  cursor: pointer;
}
.changed td:first-child {
  color: var(--accent);
}
.press {
  color: var(--accent);
  font-size: 11px;
}
.tiny {
  width: 20px;
  height: 18px;
  padding: 0;
  margin-left: 4px;
}
.actions .spacer {
  flex: 1;
}
</style>
